/**
 * Redux Slice - Módulo de Control de Cuotas
 * 
 * Gestiona el estado del módulo de cuotas incluyendo:
 * - Configuración y año activo
 * - Tipos de cuota (mensuales y no mensuales)
 * - Bomberos en el módulo de cuotas
 * - Pagos
 * - Exenciones y descuentos
 * - Estadísticas
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cuotasApi from '../../services/cuotasApi';

// ========================
// ESTADO INICIAL
// ========================

const initialState = {
  // Configuración
  configuracion: null,
  añosDisponibles: [],
  añoActivo: null, // Se carga desde la configuración del backend
  
  // Tipos de cuota
  tiposCuota: [],
  tiposCuotaLoading: false,
  
  // Tipos de bombero
  tiposBombero: [],
  tiposBomberoLoading: false,
  
  // Bomberos en cuotas (dashboard)
  bomberosData: [],
  bomberosLoading: false,
  bomberosTotal: 0,
  
  // Pagos
  pagos: [],
  pagosLoading: false,
  pagosTotal: 0,
  
  // Exenciones
  exenciones: [],
  exencionesLoading: false,
  
  // Estadísticas
  estadisticas: null,
  estadisticasLoading: false,
  morosos: null,
  morososLoading: false,
  recaudacion: null,
  recaudacionLoading: false,

  // Estadísticas por tipo de bombero
  estadisticasPorTipo: null,
  estadisticasPorTipoLoading: false,

  // Historial de bombero (para dialog)
  historialBombero: null,
  historialBomberoLoading: false,
  dialogHistorial: {
    open: false,
    bombero: null
  },
  
  // Sincronización
  syncStatus: null,
  syncLoading: false,
  
  // Estado general
  loading: false,
  error: null,
  successMessage: null,
  
  // Dialog states
  dialogPago: {
    open: false,
    bombero: null,
    tipoCuota: null,
    cuotasConDescuento: [], // Cuotas con descuentos aplicados para el bombero
    loading: false
  },
  dialogExencion: {
    open: false,
    exencion: null
  },
  dialogNuevoAño: {
    open: false,
    preview: null
  }
};

// ========================
// HELPERS
// ========================

/**
 * Extrae el mensaje de error de diferentes estructuras de respuesta
 * El interceptor de axios puede devolver:
 * - Un objeto con {message: '...'}
 * - Un string directo
 * - Un objeto con {success: false, message: '...'}
 */
const getErrorMessage = (error, defaultMessage) => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  return defaultMessage;
};

// ========================
// ASYNC THUNKS
// ========================

// --- Configuración ---

export const fetchConfiguracion = createAsyncThunk(
  'cuotas/fetchConfiguracion',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getConfiguracion();
      const data = response.data?.data || response.data;
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Error al obtener configuración'));
    }
  }
);

export const fetchAñosDisponibles = createAsyncThunk(
  'cuotas/fetchAñosDisponibles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getAños();
      const data = response.data?.data || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Error al obtener años'));
    }
  }
);

export const crearNuevoAño = createAsyncThunk(
  'cuotas/crearNuevoAño',
  async (datos, { rejectWithValue, dispatch }) => {
    try {
      const response = await cuotasApi.crearNuevoAño(datos);
      // Recargar datos después de crear año
      dispatch(fetchAñosDisponibles());
      dispatch(fetchConfiguracion());
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Error al crear año'));
    }
  }
);

export const fetchPreviewNuevoAño = createAsyncThunk(
  'cuotas/fetchPreviewNuevoAño',
  async (año, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getPreviewNuevoAño(año);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener preview');
    }
  }
);

// --- Tipos de Cuota ---

export const fetchTiposCuota = createAsyncThunk(
  'cuotas/fetchTiposCuota',
  async ({ año, esMensual, activa } = {}, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getTiposCuota({ año, esMensual, activa });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener tipos de cuota');
    }
  }
);

// --- Tipos de Bombero ---

export const fetchTiposBombero = createAsyncThunk(
  'cuotas/fetchTiposBombero',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getTiposBombero();
      return response.data.data || response.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener tipos de bombero');
    }
  }
);

export const crearTipoCuota = createAsyncThunk(
  'cuotas/crearTipoCuota',
  async (datos, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.crearTipoCuota(datos);
      // Recargar tipos de cuota
      const { añoActivo } = getState().cuotas;
      dispatch(fetchTiposCuota({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al crear tipo de cuota');
    }
  }
);

export const actualizarTipoCuota = createAsyncThunk(
  'cuotas/actualizarTipoCuota',
  async ({ id, datos }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.updateTipoCuota(id, datos);
      // Recargar tipos de cuota
      const { añoActivo } = getState().cuotas;
      dispatch(fetchTiposCuota({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al actualizar tipo de cuota');
    }
  }
);

export const actualizarConfiguracion = createAsyncThunk(
  'cuotas/actualizarConfiguracion',
  async (datos, { rejectWithValue, dispatch }) => {
    try {
      const response = await cuotasApi.updateConfiguracion(datos);
      dispatch(fetchConfiguracion());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al actualizar configuración');
    }
  }
);

export const actualizarMontoCuota = createAsyncThunk(
  'cuotas/actualizarMontoCuota',
  async ({ id, nuevoMonto }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.actualizarMontoCuota(id, nuevoMonto);
      // Recargar datos
      const { añoActivo } = getState().cuotas;
      dispatch(fetchTiposCuota({ año: añoActivo }));
      dispatch(fetchBomberosData({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al actualizar monto');
    }
  }
);

// --- Bomberos (Dashboard) ---

export const fetchBomberosData = createAsyncThunk(
  'cuotas/fetchBomberosData',
  async ({ año, page = 1, limit = 50, filtros = {} } = {}, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getBomberos({ año, page, limit, ...filtros });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Error al obtener bomberos'));
    }
  }
);

export const fetchBomberoDetalle = createAsyncThunk(
  'cuotas/fetchBomberoDetalle',
  async (id, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getBomberoDetalle(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener detalle del bombero');
    }
  }
);

// Eliminar bombero del módulo de cuotas (soft delete)
export const eliminarBomberoDeCuotas = createAsyncThunk(
  'cuotas/eliminarBomberoDeCuotas',
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await cuotasApi.eliminarBomberoDeCuotas(id);
      // Refrescar lista de bomberos y estadísticas
      const { añoActivo } = getState().cuotas;
      dispatch(fetchBomberosData({ año: añoActivo }));
      dispatch(fetchEstadisticas({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al eliminar bombero');
    }
  }
);

// Thunk para abrir el diálogo de pago cargando las cuotas con descuentos
export const openDialogPagoWithDescuentos = createAsyncThunk(
  'cuotas/openDialogPagoWithDescuentos',
  async ({ bombero, tipoCuota = null }, { rejectWithValue, getState }) => {
    try {
      // Obtener el año activo del state del frontend
      const { añoActivo } = getState().cuotas;
      // Cargar detalle del bombero con cuotas y descuentos aplicados para el año visualizado
      const response = await cuotasApi.getBomberoDetalle(bombero.id, { año: añoActivo });
      return {
        bombero,
        tipoCuota,
        bomberoDetalle: response.data
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al cargar datos del bombero');
    }
  }
);

// --- Pagos ---

export const fetchPagos = createAsyncThunk(
  'cuotas/fetchPagos',
  async (filtros = {}, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getPagos(filtros);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener pagos');
    }
  }
);

export const registrarPago = createAsyncThunk(
  'cuotas/registrarPago',
  async (datos, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.registrarPago(datos);
      // Recargar datos
      const { añoActivo } = getState().cuotas;
      dispatch(fetchBomberosData({ año: añoActivo }));
      dispatch(fetchEstadisticas({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al registrar pago');
    }
  }
);

export const registrarPagoMultiple = createAsyncThunk(
  'cuotas/registrarPagoMultiple',
  async (datos, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.registrarPagoMultiple(datos);
      // Recargar datos
      const { añoActivo } = getState().cuotas;
      dispatch(fetchBomberosData({ año: añoActivo }));
      dispatch(fetchEstadisticas({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al registrar pagos');
    }
  }
);

export const anularPago = createAsyncThunk(
  'cuotas/anularPago',
  async ({ id, motivo }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.anularPago(id, motivo);
      // Recargar datos
      const { añoActivo } = getState().cuotas;
      dispatch(fetchBomberosData({ año: añoActivo }));
      dispatch(fetchEstadisticas({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al anular pago');
    }
  }
);

// --- Exenciones ---

export const fetchExenciones = createAsyncThunk(
  'cuotas/fetchExenciones',
  async ({ año, tipoExencion, estado } = {}, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getExenciones({ año, tipoExencion, estado });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener exenciones');
    }
  }
);

export const crearExencion = createAsyncThunk(
  'cuotas/crearExencion',
  async (datos, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.crearExencion(datos);
      // Recargar exenciones y estadísticas
      const { añoActivo } = getState().cuotas;
      dispatch(fetchExenciones({ año: añoActivo }));
      dispatch(fetchEstadisticas({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al crear exención');
    }
  }
);

export const finalizarExencion = createAsyncThunk(
  'cuotas/finalizarExencion',
  async ({ id, motivo }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.finalizarExencion(id, motivo);
      // Recargar exenciones y estadísticas
      const { añoActivo } = getState().cuotas;
      dispatch(fetchExenciones({ año: añoActivo }));
      dispatch(fetchEstadisticas({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al finalizar exención');
    }
  }
);

export const actualizarExencion = createAsyncThunk(
  'cuotas/actualizarExencion',
  async ({ id, datos }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.updateExencion(id, datos);
      // Recargar exenciones y estadísticas
      const { añoActivo } = getState().cuotas;
      dispatch(fetchExenciones({ año: añoActivo }));
      dispatch(fetchEstadisticas({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al actualizar exención');
    }
  }
);

export const eliminarExencion = createAsyncThunk(
  'cuotas/eliminarExencion',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.eliminarExencion(id);
      // Recargar exenciones y estadísticas
      const { añoActivo } = getState().cuotas;
      dispatch(fetchExenciones({ año: añoActivo }));
      dispatch(fetchEstadisticas({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al eliminar exención');
    }
  }
);

// --- Estadísticas ---

export const fetchEstadisticas = createAsyncThunk(
  'cuotas/fetchEstadisticas',
  async ({ año } = {}, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getEstadisticas(año);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener estadísticas');
    }
  }
);

export const fetchMorosos = createAsyncThunk(
  'cuotas/fetchMorosos',
  async ({ año, limite = 100 } = {}, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getMorosos(año, limite);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener morosos');
    }
  }
);

export const fetchRecaudacion = createAsyncThunk(
  'cuotas/fetchRecaudacion',
  async ({ año } = {}, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getRecaudacion(año);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener recaudación');
    }
  }
);

export const fetchEstadisticasPorTipo = createAsyncThunk(
  'cuotas/fetchEstadisticasPorTipo',
  async ({ año } = {}, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getEstadisticasPorTipo(año);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener estadísticas por tipo');
    }
  }
);

export const fetchBomberoHistorial = createAsyncThunk(
  'cuotas/fetchBomberoHistorial',
  async ({ id, año } = {}, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getBomberoHistorial(id, { año });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener historial');
    }
  }
);

export const ajustarAbono = createAsyncThunk(
  'cuotas/ajustarAbono',
  async ({ id, monto, motivo }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.ajustarAbono(id, { monto, motivo });
      const { añoActivo } = getState().cuotas;
      dispatch(fetchBomberosData({ año: añoActivo }));
      dispatch(fetchEstadisticas({ año: añoActivo }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al ajustar abono');
    }
  }
);

// --- Sincronización ---

export const fetchSyncStatus = createAsyncThunk(
  'cuotas/fetchSyncStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.getSyncStatus();
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener estado de sincronización');
    }
  }
);

export const ejecutarSincronizacion = createAsyncThunk(
  'cuotas/ejecutarSincronizacion',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cuotasApi.sincronizarTodo();
      // Recargar datos
      const { añoActivo } = getState().cuotas;
      dispatch(fetchBomberosData({ año: añoActivo }));
      dispatch(fetchSyncStatus());
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Error al sincronizar'));
    }
  }
);

// --- Exportación ---

export const exportarExcel = createAsyncThunk(
  'cuotas/exportarExcel',
  async (año, { rejectWithValue }) => {
    try {
      const response = await cuotasApi.exportarExcel(año);
      
      // Crear blob y descargar
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cuotas_${año}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al exportar');
    }
  }
);

// ========================
// SLICE
// ========================

const cuotasSlice = createSlice({
  name: 'cuotas',
  initialState,
  reducers: {
    // Cambiar año activo
    setAñoActivo: (state, action) => {
      state.añoActivo = action.payload;
    },
    
    // Limpiar errores
    clearError: (state) => {
      state.error = null;
    },
    
    // Limpiar mensaje de éxito
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    
    // Dialog de pago (versión simple sin descuentos - mantener por compatibilidad)
    openDialogPago: (state, action) => {
      state.dialogPago.open = true;
      // action.payload puede ser { bombero, tipoCuota } o solo el bombero
      state.dialogPago.bombero = action.payload?.bombero || action.payload;
      state.dialogPago.tipoCuota = action.payload?.tipoCuota || null;
      state.dialogPago.cuotasConDescuento = [];
    },
    closeDialogPago: (state) => {
      state.dialogPago.open = false;
      state.dialogPago.bombero = null;
      state.dialogPago.tipoCuota = null;
      state.dialogPago.cuotasConDescuento = [];
      state.dialogPago.loading = false;
    },
    
    // Dialog de exención
    openDialogExencion: (state, action) => {
      state.dialogExencion.open = true;
      state.dialogExencion.exencion = action.payload || null;
    },
    closeDialogExencion: (state) => {
      state.dialogExencion.open = false;
      state.dialogExencion.exencion = null;
    },
    
    // Dialog de nuevo año
    openDialogNuevoAño: (state) => {
      state.dialogNuevoAño.open = true;
      state.dialogNuevoAño.preview = null;
    },
    closeDialogNuevoAño: (state) => {
      state.dialogNuevoAño.open = false;
      state.dialogNuevoAño.preview = null;
    },

    // Dialog de historial de bombero
    openDialogHistorial: (state, action) => {
      state.dialogHistorial.open = true;
      state.dialogHistorial.bombero = action.payload;
      state.historialBombero = null;
    },
    closeDialogHistorial: (state) => {
      state.dialogHistorial.open = false;
      state.dialogHistorial.bombero = null;
      state.historialBombero = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // --- Configuración ---
      .addCase(fetchConfiguracion.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConfiguracion.fulfilled, (state, action) => {
        state.loading = false;
        state.configuracion = action.payload;
        if (action.payload?.añoActivo) {
          state.añoActivo = action.payload.añoActivo;
        }
      })
      .addCase(fetchConfiguracion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchAñosDisponibles.fulfilled, (state, action) => {
        state.añosDisponibles = action.payload;
      })
      
      .addCase(crearNuevoAño.pending, (state) => {
        state.loading = true;
      })
      .addCase(crearNuevoAño.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = `Año ${action.payload.año} creado exitosamente`;
        state.dialogNuevoAño.open = false;
        state.añoActivo = action.payload.año;
      })
      .addCase(crearNuevoAño.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchPreviewNuevoAño.fulfilled, (state, action) => {
        state.dialogNuevoAño.preview = action.payload;
      })
      
      // --- Tipos de Cuota ---
      .addCase(fetchTiposCuota.pending, (state) => {
        state.tiposCuotaLoading = true;
      })
      .addCase(fetchTiposCuota.fulfilled, (state, action) => {
        state.tiposCuotaLoading = false;
        state.tiposCuota = action.payload;
      })
      .addCase(fetchTiposCuota.rejected, (state, action) => {
        state.tiposCuotaLoading = false;
        state.error = action.payload;
      })
      
      // --- Tipos de Bombero ---
      .addCase(fetchTiposBombero.pending, (state) => {
        state.tiposBomberoLoading = true;
      })
      .addCase(fetchTiposBombero.fulfilled, (state, action) => {
        state.tiposBomberoLoading = false;
        state.tiposBombero = action.payload;
      })
      .addCase(fetchTiposBombero.rejected, (state, action) => {
        state.tiposBomberoLoading = false;
        state.error = action.payload;
      })
      
      .addCase(actualizarMontoCuota.fulfilled, (state, action) => {
        state.successMessage = action.payload?.message || 'Monto actualizado';
      })
      
      // --- Bomberos ---
      .addCase(fetchBomberosData.pending, (state) => {
        state.bomberosLoading = true;
      })
      .addCase(fetchBomberosData.fulfilled, (state, action) => {
        state.bomberosLoading = false;
        state.bomberosData = action.payload.bomberos || action.payload;
        state.bomberosTotal = action.payload.pagination?.total || action.payload.length;
      })
      .addCase(fetchBomberosData.rejected, (state, action) => {
        state.bomberosLoading = false;
        state.error = action.payload;
      })
      
      // --- Eliminar Bombero de Cuotas ---
      .addCase(eliminarBomberoDeCuotas.pending, (state) => {
        state.loading = true;
      })
      .addCase(eliminarBomberoDeCuotas.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || 'Bombero eliminado del módulo de cuotas';
      })
      .addCase(eliminarBomberoDeCuotas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // --- Pagos ---
      .addCase(fetchPagos.pending, (state) => {
        state.pagosLoading = true;
      })
      .addCase(fetchPagos.fulfilled, (state, action) => {
        state.pagosLoading = false;
        state.pagos = action.payload.pagos || action.payload;
        state.pagosTotal = action.payload.pagination?.total || 0;
      })
      .addCase(fetchPagos.rejected, (state, action) => {
        state.pagosLoading = false;
        state.error = action.payload;
      })
      
      .addCase(registrarPago.pending, (state) => {
        state.loading = true;
      })
      .addCase(registrarPago.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Pago registrado exitosamente';
        state.dialogPago.open = false;
      })
      .addCase(registrarPago.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(registrarPagoMultiple.fulfilled, (state, action) => {
        state.loading = false;
        // El payload real viene anidado (axios → {data:{pagosCreados}}). Leer ambos niveles
        // para no mostrar siempre "0 pagos registrados".
        const n = action.payload?.data?.pagosCreados?.length ?? action.payload?.pagosCreados?.length ?? 0;
        state.successMessage = `${n} pagos registrados`;
        state.dialogPago.open = false;
      })
      
      .addCase(anularPago.fulfilled, (state) => {
        state.successMessage = 'Pago anulado exitosamente';
      })
      
      // --- Exenciones ---
      .addCase(fetchExenciones.pending, (state) => {
        state.exencionesLoading = true;
      })
      .addCase(fetchExenciones.fulfilled, (state, action) => {
        state.exencionesLoading = false;
        state.exenciones = action.payload;
      })
      .addCase(fetchExenciones.rejected, (state, action) => {
        state.exencionesLoading = false;
        state.error = action.payload;
      })
      
      .addCase(crearExencion.fulfilled, (state) => {
        state.successMessage = 'Exención creada exitosamente';
        state.dialogExencion.open = false;
      })
      
      .addCase(finalizarExencion.fulfilled, (state) => {
        state.successMessage = 'Exención finalizada';
      })
      
      // --- Estadísticas ---
      .addCase(fetchEstadisticas.pending, (state) => {
        state.estadisticasLoading = true;
      })
      .addCase(fetchEstadisticas.fulfilled, (state, action) => {
        state.estadisticasLoading = false;
        state.estadisticas = action.payload;
      })
      .addCase(fetchEstadisticas.rejected, (state, action) => {
        state.estadisticasLoading = false;
        state.error = action.payload;
      })

      // --- Morosos ---
      .addCase(fetchMorosos.pending, (state) => {
        state.morososLoading = true;
      })
      .addCase(fetchMorosos.fulfilled, (state, action) => {
        state.morososLoading = false;
        state.morosos = action.payload;
      })
      .addCase(fetchMorosos.rejected, (state, action) => {
        state.morososLoading = false;
        state.error = action.payload;
      })

      // --- Recaudación ---
      .addCase(fetchRecaudacion.pending, (state) => {
        state.recaudacionLoading = true;
      })
      .addCase(fetchRecaudacion.fulfilled, (state, action) => {
        state.recaudacionLoading = false;
        state.recaudacion = action.payload;
      })
      .addCase(fetchRecaudacion.rejected, (state, action) => {
        state.recaudacionLoading = false;
        state.error = action.payload;
      })

      // --- Estadísticas por tipo de bombero ---
      .addCase(fetchEstadisticasPorTipo.pending, (state) => {
        state.estadisticasPorTipoLoading = true;
      })
      .addCase(fetchEstadisticasPorTipo.fulfilled, (state, action) => {
        state.estadisticasPorTipoLoading = false;
        state.estadisticasPorTipo = action.payload;
      })
      .addCase(fetchEstadisticasPorTipo.rejected, (state) => {
        state.estadisticasPorTipoLoading = false;
      })

      // --- Historial Bombero ---
      .addCase(fetchBomberoHistorial.pending, (state) => {
        state.historialBomberoLoading = true;
      })
      .addCase(fetchBomberoHistorial.fulfilled, (state, action) => {
        state.historialBomberoLoading = false;
        state.historialBombero = action.payload;
      })
      .addCase(fetchBomberoHistorial.rejected, (state, action) => {
        state.historialBomberoLoading = false;
        state.error = action.payload;
      })

      // --- Ajustar Abono ---
      .addCase(ajustarAbono.fulfilled, (state, action) => {
        state.successMessage = action.payload?.message || 'Abono ajustado exitosamente';
        if (state.historialBombero) {
          // Forzar recarga al reabrir
          state.historialBombero = null;
        }
      })
      .addCase(ajustarAbono.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // --- Sincronización ---
      .addCase(fetchSyncStatus.fulfilled, (state, action) => {
        state.syncStatus = action.payload;
      })
      
      .addCase(ejecutarSincronizacion.pending, (state) => {
        state.syncLoading = true;
      })
      .addCase(ejecutarSincronizacion.fulfilled, (state, action) => {
        state.syncLoading = false;
        state.successMessage = 'Sincronización completada';
      })
      .addCase(ejecutarSincronizacion.rejected, (state, action) => {
        state.syncLoading = false;
        state.error = action.payload;
      })
      
      // --- Exportación ---
      .addCase(exportarExcel.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportarExcel.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Archivo Excel exportado exitosamente';
      })
      .addCase(exportarExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // --- Dialog de Pago con Descuentos ---
      .addCase(openDialogPagoWithDescuentos.pending, (state) => {
        state.dialogPago.loading = true;
        state.dialogPago.open = true;
      })
      .addCase(openDialogPagoWithDescuentos.fulfilled, (state, action) => {
        state.dialogPago.loading = false;
        state.dialogPago.open = true;
        state.dialogPago.bombero = action.payload.bombero;
        state.dialogPago.tipoCuota = action.payload.tipoCuota;
        // Guardar las cuotas con descuentos aplicados
        const detalle = action.payload.bomberoDetalle?.data;
        state.dialogPago.cuotasConDescuento = detalle?.estadoPorCuota || [];
      })
      .addCase(openDialogPagoWithDescuentos.rejected, (state, action) => {
        state.dialogPago.loading = false;
        state.dialogPago.open = false;
        state.error = action.payload;
      });
  }
});

// Exportar actions
export const {
  setAñoActivo,
  clearError,
  clearSuccessMessage,
  openDialogPago,
  closeDialogPago,
  openDialogExencion,
  closeDialogExencion,
  openDialogNuevoAño,
  closeDialogNuevoAño,
  openDialogHistorial,
  closeDialogHistorial
} = cuotasSlice.actions;

// Exportar reducer
export default cuotasSlice.reducer;

// ========================
// SELECTORES
// ========================

// Helper para asegurar que un valor sea array
const ensureArray = (value) => Array.isArray(value) ? value : [];

// Configuración
export const selectConfiguracion = (state) => state.cuotas.configuracion;
export const selectAñoActivo = (state) => state.cuotas.añoActivo;
export const selectAñosDisponibles = (state) => ensureArray(state.cuotas.añosDisponibles);

// Tipos de cuota
export const selectTiposCuota = (state) => ensureArray(state.cuotas.tiposCuota);
export const selectTiposCuotaMensuales = (state) => 
  ensureArray(state.cuotas.tiposCuota).filter(t => t.esMensual).sort((a, b) => a.mes - b.mes);
export const selectTiposCuotaNoMensuales = (state) => 
  ensureArray(state.cuotas.tiposCuota).filter(t => !t.esMensual);

// Tipos de Bombero
export const selectTiposBombero = (state) => ensureArray(state.cuotas.tiposBombero);
export const selectTiposBomberoLoading = (state) => state.cuotas.tiposBomberoLoading;

// Bomberos
export const selectBomberosData = (state) => ensureArray(state.cuotas.bomberosData);
export const selectBomberosLoading = (state) => state.cuotas.bomberosLoading;

// Pagos
export const selectPagos = (state) => ensureArray(state.cuotas.pagos);

// Exenciones
export const selectExenciones = (state) => ensureArray(state.cuotas.exenciones);
export const selectExencionesActivas = (state) => 
  ensureArray(state.cuotas.exenciones).filter(e => e.estado === 'ACTIVA');

// Estadísticas
export const selectEstadisticas = (state) => state.cuotas.estadisticas;

// Estado general
export const selectLoading = (state) => state.cuotas.loading;
export const selectError = (state) => state.cuotas.error;
export const selectSuccessMessage = (state) => state.cuotas.successMessage;
export const selectSyncLoading = (state) => state.cuotas.syncLoading;

export const selectEstadisticasPorTipo = (state) => state.cuotas.estadisticasPorTipo;
export const selectEstadisticasPorTipoLoading = (state) => state.cuotas.estadisticasPorTipoLoading;

// Morosos y recaudación
export const selectMorosos = (state) => state.cuotas.morosos;
export const selectMorososLoading = (state) => state.cuotas.morososLoading;
export const selectRecaudacion = (state) => state.cuotas.recaudacion;
export const selectRecaudacionLoading = (state) => state.cuotas.recaudacionLoading;

// Historial de bombero
export const selectHistorialBombero = (state) => state.cuotas.historialBombero;
export const selectHistorialBomberoLoading = (state) => state.cuotas.historialBomberoLoading;

// Dialogs
export const selectDialogPago = (state) => state.cuotas.dialogPago;
export const selectDialogExencion = (state) => state.cuotas.dialogExencion;
export const selectDialogNuevoAño = (state) => state.cuotas.dialogNuevoAño;
export const selectDialogHistorial = (state) => state.cuotas.dialogHistorial;
